import React, { useState } from 'react';
import { getIntl } from '../../../utils/intl';
import { ActionButtonLayout } from '../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import Checkbox from '../../v2BaseComponents/Inputs/Checkbox';
import { UserIntent } from '../../../primary-record-types/DynamoTypes';
import { Dynamo } from '../../../primary-record-types';

const intl = getIntl('UserIntent', {
    welcome: 'Welcome!',
    planOnUsing:
        'We would love to send tips on how best to use Primary Record! Tell us how you will be using Primary Record! ',
    manageOwn: 'To manage my health information',
    manageFamily: 'To manage health information for my family',
    collaborate: 'To support someone else',
    continue: 'Continue',
    skip: 'Skip',
});

export type UserIntentsFormOwnProps = {
    onSubmit: (intents: Dynamo.UserIntent[]) => void;
    onSkip: () => void;
};

export type UserIntentsProps = UserIntentsFormOwnProps;

const UserIntentsForm: React.FC<UserIntentsProps> = ({ onSubmit, onSkip }) => {
    const [intentsSelection, setIntentsSelection] = useState<UserIntent[] | null>(null);

    const isMobile = useIsMobileLayout();

    const handleSelection = (selection: UserIntent) => {
        setIntentsSelection((prev) => {
            if (!prev) return [selection];
            if (prev.includes(selection)) return prev.filter((intent) => intent !== selection);
            return [...prev, selection];
        });
    };

    return (
        <ActionButtonLayout
            scrollable
            whiteBackground
            actions={[
                {
                    title: intl('skip'),
                    onPress: () => onSkip(),
                    testID: 'intent-skip',
                    icon: 'ArrowRight',
                    variant: 'secondary',
                },
                {
                    icon: 'CheckCircle',
                    title: intl('continue'),
                    onPress: () => onSubmit(intentsSelection!),
                    testID: 'intent-continue',
                    isDisabled: !intentsSelection?.length,
                },
            ]}
        >
            <MainContentContainer>
                <ContentStack>
                    <Stack horizontal justify="center" fullWidth>
                        <Stack vertical align="flex-start" style={{ maxWidth: 400 }} fullWidth>
                            {isMobile ? null : (
                                <Text style={{ marginBottom: 5 }} variant="h2">
                                    {intl('welcome')}
                                </Text>
                            )}
                            <Text style={{ marginBottom: 20 }} variant="subtitle-1">
                                {intl('planOnUsing')}
                            </Text>

                            <Stack vertical fullWidth space={5}>
                                <Checkbox
                                    title={intl('manageOwn')}
                                    value={!!intentsSelection?.includes('manage-self')}
                                    testID="manage-own-checkbox"
                                    onChange={() => {
                                        handleSelection('manage-self');
                                    }}
                                />
                                <Checkbox
                                    title={intl('manageFamily')}
                                    value={!!intentsSelection?.includes('manage-family')}
                                    testID="manage-family-checkbox"
                                    onChange={() => {
                                        handleSelection('manage-family');
                                    }}
                                />
                                <Checkbox
                                    title={intl('collaborate')}
                                    value={!!intentsSelection?.includes('collaborator-use')}
                                    testID="collaborate-checkbox"
                                    onChange={() => {
                                        handleSelection('collaborator-use');
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                </ContentStack>
            </MainContentContainer>
        </ActionButtonLayout>
    );
};

export default React.memo(UserIntentsForm);
